import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { clientsService } from '../services/clients.service';
import type { ClientsResponse, ClientFilters, Client } from '@lnr-v2/types';
import { onError, onSettled } from '../lib/optimisticHelpers';

// Re-export types for backwards compatibility
export type { ClientsResponse, ClientFilters };

interface UseClientsOptions {
  initialFilters?: ClientFilters;
  autoFetch?: boolean;
}

interface UseClientsReturn {
  data: ClientsResponse | null;
  loading: boolean;
  error: Error | null;
  filters: ClientFilters;
  setFilters: (filters: ClientFilters) => void;
  updateFilter: <K extends keyof ClientFilters>(
    key: K,
    value: ClientFilters[K]
  ) => void;
  refetch: () => Promise<void>;
  createClient: (data: Partial<Client>) => Promise<void>;
  updateClient: (id: string, data: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
}

/**
 * Custom hook for managing clients with React Query
 *
 * Benefits over previous implementation:
 * - Automatic request deduplication based on filters
 * - Intelligent caching (5 min stale time, 10 min cache time)
 * - Automatic background refetching
 * - Better error handling and retry logic
 * - Filter-based cache keys for pagination
 * - DevTools for debugging queries
 *
 * React Query automatically handles:
 * - Preventing duplicate concurrent requests with same filters
 * - Caching responses based on filter parameters
 * - Background refetching when data becomes stale
 * - Request cancellation
 * - Error retry with exponential backoff
 *
 * @param options - Hook options with initialFilters and autoFetch
 */
export const useClients = ({
  initialFilters = {
    page: 1,
    limit: 10,
    sortBy: 'sortOrder',
    sortOrder: 'ASC',
  },
  autoFetch = true,
}: UseClientsOptions = {}): UseClientsReturn => {
  const queryClient = useQueryClient();

  // Local state for filters (controlled by component)
  const [filters, setFilters] = useState<ClientFilters>(initialFilters);

  // Main query for fetching clients
  // React Query will cache results based on filter parameters
  const {
    data = null,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.clients.list(filters),
    queryFn: () => clientsService.getAll(filters),
    // Only fetch if autoFetch is true
    enabled: autoFetch,
    // Additional options (defaults from queryClient are already optimal)
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    // Keep previous data while fetching new page (better UX for pagination)
    placeholderData: (previousData) => previousData,
  });

  // Convert query error to Error type for backwards compatibility
  const error = queryError ? (queryError as Error) : null;

  // Client mutations with optimistic updates
  // For paginated data, we update the current page's cache
  const createClientMutation = useMutation({
    mutationFn: clientsService.create,
    onMutate: async (newClient) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.clients.list(filters),
      });

      const previousData = queryClient.getQueryData<ClientsResponse>(
        queryKeys.clients.list(filters)
      );

      // Create optimistic client with default values
      const now = new Date();
      const optimisticClient: Client = {
        ...newClient,
        id: `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        slug: newClient.name?.toLowerCase().replace(/\s+/g, '-') || '',
        tags: newClient.tags || [],
        isActive: newClient.isActive ?? true,
        isFeatured: newClient.isFeatured ?? false,
        createdById: 'current-user', // Will be replaced by real value
        projectCount: 0,
        createdAt: now,
        updatedAt: now,
      } as Client;

      // Add optimistic client to the current page
      queryClient.setQueryData<ClientsResponse>(
        queryKeys.clients.list(filters),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: [optimisticClient, ...old.items],
            pagination: {
              ...old.pagination,
              total: old.pagination.total + 1,
            },
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.clients.list(filters), context);
    },
    onSuccess: (data) => {
      // Replace optimistic client with real data
      queryClient.setQueryData<ClientsResponse>(
        queryKeys.clients.list(filters),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((client) =>
              client.id.startsWith('temp-') ? data : client
            ),
          };
        }
      );
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.clients.all);
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Client> }) =>
      clientsService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.clients.list(filters),
      });

      const previousData = queryClient.getQueryData<ClientsResponse>(
        queryKeys.clients.list(filters)
      );

      // Update the client in the current page
      queryClient.setQueryData<ClientsResponse>(
        queryKeys.clients.list(filters),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((client) =>
              client.id === id
                ? {
                    ...client,
                    ...data,
                    updatedAt: new Date(),
                  }
                : client
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.clients.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.clients.all);
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: clientsService.delete,
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.clients.list(filters),
      });

      const previousData = queryClient.getQueryData<ClientsResponse>(
        queryKeys.clients.list(filters)
      );

      // Remove the client from the current page
      queryClient.setQueryData<ClientsResponse>(
        queryKeys.clients.list(filters),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((client) => client.id !== id),
            pagination: {
              ...old.pagination,
              total: old.pagination.total - 1,
            },
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      onError(queryClient, queryKeys.clients.list(filters), context);
    },
    onSettled: () => {
      onSettled(queryClient, queryKeys.clients.all);
    },
  });

  // Update filter with automatic page reset
  const updateFilter = useCallback(
    <K extends keyof ClientFilters>(key: K, value: ClientFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        // Reset to page 1 when changing filters (except when changing page itself)
        ...(key !== 'page' ? { page: 1 } : {}),
      }));
    },
    []
  );

  // Wrapper functions to maintain backwards compatibility
  const createClient = async (data: Partial<Client>) => {
    await createClientMutation.mutateAsync(data);
  };

  const updateClient = async (id: string, data: Partial<Client>) => {
    await updateClientMutation.mutateAsync({ id, data });
  };

  const deleteClient = async (id: string) => {
    await deleteClientMutation.mutateAsync(id);
  };

  // Wrapper for refetch to match old signature
  const refetchWrapper = async () => {
    await refetch();
  };

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    updateFilter,
    refetch: refetchWrapper,
    createClient,
    updateClient,
    deleteClient,
  };
};
